import CardHeader from "components/CardHeader";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { RootState } from "../../store/store";
import { getExpenses } from "store/actions/Expenses";
import PageMeta from "components/PageMeta";
import { Card, CardBody, Col, Row } from "reactstrap";
import ExpenseAddButton from "./ExpenseAddButton";
import ExpenseRowIndex from "./ExpenseRowIndex";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { ExpenseState } from 'store/reducers/Expenses';
import { AccountState } from "store/reducers/Accounts";
import { getAccounts } from "store/actions/Accounts";

interface Props {
    accountState: AccountState;
    expenseState: ExpenseState;
}


interface Actions {
    getAccounts: () => void;
    getExpenses: () => void;
}

const DesktopExpenseRowHeight = 92;
const MobileExpenseRowHeight = 184;
const ExpenseListFallbackHeight = 640;
const ExpenseListOverscan = 6;
const MobileExpenseMediaQuery = "(max-width: 767.98px)";

const isMobileExpenseViewport = () =>
    typeof window.matchMedia === "function" && window.matchMedia(MobileExpenseMediaQuery).matches;

const Expenses: React.FC<Props & Actions> = ({ accountState, expenseState, getAccounts, getExpenses }) => {
    const { t } = useOfficeTranslation();

    const [nameKeyword, setNameKeyword] = useState("")
    const [scrollTop, setScrollTop] = useState(0)
    const [viewportHeight, setViewportHeight] = useState(ExpenseListFallbackHeight)
    const [mobileRows, setMobileRows] = useState(isMobileExpenseViewport)
    const listRef = useRef<HTMLDivElement | null>(null)

    //@ts-ignore
    var url = new URL(window.location);
    const searchTerm = url.searchParams.get('q')

    useEffect(() => {
        getAccounts()
        getExpenses()
        // eslint-disable-next-line
        if (searchTerm != undefined) {
            //@ts-ignore
            setNameKeyword(searchTerm);
        }
        // eslint-disable-next-line
    }, [getAccounts, getExpenses])

    useEffect(() => {
        if (typeof window.matchMedia !== "function") {
            return;
        }

        const media = window.matchMedia(MobileExpenseMediaQuery);
        const update = () => setMobileRows(media.matches);
        update();
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, []);

    const filteredExpenses = useMemo(() => {
        const keyword = nameKeyword.toLowerCase();

        return [...expenseState.getExpenses()]
            .filter(expense =>
                expense.name.toLowerCase().includes(keyword) ||
                (accountState.getAccountById(expense.account_id)?.legal_name ?? "").toLowerCase().includes(keyword)
            )
            .sort((a, b) => b.GetDate().getTime() - a.GetDate().getTime());
    }, [accountState, expenseState, nameKeyword]);

    useEffect(() => {
        const listElement = listRef.current;
        if (!listElement) {
            return;
        }

        const updateViewportHeight = () => {
            setViewportHeight(listElement.clientHeight || ExpenseListFallbackHeight);
        };

        updateViewportHeight();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateViewportHeight);
            return () => window.removeEventListener("resize", updateViewportHeight);
        }

        const resizeObserver = new ResizeObserver(updateViewportHeight);
        resizeObserver.observe(listElement);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        const listElement = listRef.current;
        if (!listElement) {
            return;
        }

        listElement.scrollTop = 0;
        setScrollTop(0);
    }, [mobileRows, nameKeyword]);

    const expenseRowHeight = mobileRows ? MobileExpenseRowHeight : DesktopExpenseRowHeight;
    const startIndex = Math.max(0, Math.floor(scrollTop / expenseRowHeight) - ExpenseListOverscan);
    const visibleRowCount = Math.ceil(viewportHeight / expenseRowHeight) + ExpenseListOverscan * 2;
    const endIndex = Math.min(filteredExpenses.length, startIndex + visibleRowCount);
    const visibleExpenses = filteredExpenses.slice(startIndex, endIndex);
    const topSpacerHeight = startIndex * expenseRowHeight;
    const bottomSpacerHeight = Math.max(0, (filteredExpenses.length - endIndex) * expenseRowHeight);

    return (
        <div className="page-content">
            <PageMeta title={t("common.expenses")} />

            <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                    <h4 className="mb-1">{t("common.expenses")}</h4>
                    <p className="text-muted mb-0">{t("expenses.expenses.expensesSuppliersAndLedgerAllocation")}</p>
                </div>
                <ExpenseAddButton />
            </div>

            <Card>
                <CardBody>
                    <CardHeader
                        title={t("expenses.expenses.expenseOverview")}
                        icon="mdi-cash-minus"
                        count={filteredExpenses.length}
                    />

                    <Row className="g-3 py-3 border-bottom">
                        <Col md={5}>
                            <div className="position-relative">
                                <input
                                    aria-label={t("expenses.expenses.searchExpenses")}
                                    className="form-control ps-5"
                                    id="search"
                                    name="search"
                                    onChange={event => {
                                        const url = new URL(window.location.href);
                                        if (event.target.value) {
                                            url.searchParams.set("q", event.target.value);
                                        } else {
                                            url.searchParams.delete("q");
                                        }
                                        window.history.replaceState({}, "", url);
                                        setNameKeyword(event.target.value);
                                    }}
                                    placeholder={t("expenses.expenses.searchExpenseOrSupplier")}
                                    type="text"
                                    value={nameKeyword}
                                />
                                <i
                                    className="mdi mdi-magnify position-absolute text-muted font-size-20"
                                    style={{ left: 16, top: 7 }}
                                />
                            </div>
                        </Col>
                    </Row>

                    {filteredExpenses.length > 0
                        ? <div
                            ref={listRef}
                            style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto" }}
                            onScroll={event => setScrollTop(event.currentTarget.scrollTop)}
                        >
                            {topSpacerHeight > 0 &&
                                <div aria-hidden="true" style={{ height: topSpacerHeight }} />
                            }
                            {visibleExpenses.map(expense =>
                                <ExpenseRowIndex
                                    key={expense.id}
                                    expense={expense}
                                    style={{ height: expenseRowHeight }}
                                />
                            )}
                            {bottomSpacerHeight > 0 &&
                                <div aria-hidden="true" style={{ height: bottomSpacerHeight }} />
                            }
                        </div>
                        : <p className="text-muted text-center mt-3 mb-0">{t("expenses.expenses.noExpensesFound")}</p>
                    }
                </CardBody>
            </Card>
        </div>
    )
}




const mapStateToProps = (state: RootState) => {
    return {
        accountState: state.Accounts,
        expenseState: state.Expenses,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ getAccounts, getExpenses }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(Expenses)
